import React, { useEffect, useState, useRef } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { Song } from '../data/songs';
import { SongPhoto } from './SongPhoto';
interface DryingLineProps {
  songs: Song[];
  selectedSong: Song;
  onSelect: (song: Song) => void;
}
export function DryingLine({
  songs,
  selectedSong,
  onSelect
}: DryingLineProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [scrollableWidth, setScrollableWidth] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  // Mouse-driven progress (0 = leftmost, 1 = rightmost)
  const progress = useMotionValue(0);
  const smoothProgress = useSpring(progress, {
    stiffness: 25,
    damping: 30,
    mass: 1.5
  });
  // Transform progress -> negative translateX
  const x = useTransform(smoothProgress, [0, 1], [0, -scrollableWidth]);
  useEffect(() => {
    const updateSizes = () => {
      if (trackRef.current && containerRef.current) {
        const trackWidth = trackRef.current.scrollWidth;
        const cWidth = containerRef.current.offsetWidth;
        setContainerWidth(cWidth);
        setScrollableWidth(Math.max(0, trackWidth - cWidth));
      }
    };
    updateSizes();
    window.addEventListener('resize', updateSizes);
    return () => window.removeEventListener('resize', updateSizes);
  }, [songs]);
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const relativeX = e.clientX - rect.left;
    // Large dead zone on the LEFT (40%) so the first song stays put until cursor moves far right
    // Smaller dead zone on the RIGHT (8%) for stable end-of-list browsing
    const deadZoneLeft = 0.4;
    const deadZoneRight = 0.08;
    const raw = relativeX / rect.width;
    let mapped = (raw - deadZoneLeft) / (1 - deadZoneLeft - deadZoneRight);
    mapped = Math.max(0, Math.min(1, mapped));
    progress.set(mapped);
  };
  // Keyboard navigation between songs
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      const currentIdx = songs.findIndex(s => s.id === selectedSong.id);
      if (e.key === 'ArrowRight' && currentIdx < songs.length - 1) {
        onSelect(songs[currentIdx + 1]);
      } else if (e.key === 'ArrowLeft' && currentIdx > 0) {
        onSelect(songs[currentIdx - 1]);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [songs, selectedSong, onSelect]);
  return <div className="relative w-full py-12 md:py-20" data-id="element-131">
      {/* The White Nylon Clothesline — with catenary sag */}
      <svg className="absolute left-0 top-[88px] md:top-[120px] h-8 w-full pointer-events-none" preserveAspectRatio="none" viewBox="0 0 1000 30" data-id="element-132">
        <defs data-id="element-133">
          {/* Bright white/cream gradient for nylon rope */}
          <linearGradient id="ropeGrad" x1="0%" y1="0%" x2="0%" y2="100%" data-id="element-134">
            <stop offset="0%" stopColor="#ffffff" data-id="element-135" />
            <stop offset="50%" stopColor="#f0f0f0" data-id="element-136" />
            <stop offset="100%" stopColor="#d0d0d0" data-id="element-137" />
          </linearGradient>

          {/* Subtle twist pattern for nylon rope */}
          <pattern id="twist" x="0" y="0" width="8" height="4" patternUnits="userSpaceOnUse" data-id="element-138">
            <path d="M 0 4 L 8 0" stroke="rgba(0,0,0,0.15)" strokeWidth="0.8" fill="none" data-id="element-139" />
          </pattern>
        </defs>

        {/* Drop shadow */}
        <path d="M 0 8 Q 500 22 1000 8" stroke="rgba(0,0,0,0.4)" strokeWidth="3" fill="none" transform="translate(0, 8)" opacity="0.6" filter="blur(2px)" data-id="element-140" />

        {/* Main rope body */}
        <path d="M 0 6 Q 500 20 1000 6" stroke="url(#ropeGrad)" strokeWidth="3" fill="none" strokeLinecap="round" data-id="element-141" />

        {/* Rope twist texture */}
        <path d="M 0 6 Q 500 20 1000 6" stroke="url(#twist)" strokeWidth="3" fill="none" strokeLinecap="round" data-id="element-142" />

        {/* Top highlight for 3D effect */}
        <path d="M 0 5.5 Q 500 19.5 1000 5.5" stroke="rgba(255,255,255,0.9)" strokeWidth="0.8" fill="none" data-id="element-143" />
      </svg>

      {/* Hover-scroll container (desktop). On touch devices falls back to native scroll. */}
      <div ref={containerRef} onMouseMove={handleMouseMove} className="overflow-hidden md:overflow-hidden overflow-x-auto md:cursor-default hide-scrollbar" style={{
      touchAction: 'pan-x'
    }} data-id="element-144">
        <motion.div ref={trackRef} style={{
        x
      }}
      // Padding is 50vw minus half the photo width (44/2 = 88px on mobile, 60/2 = 120px on desktop)
      // This ensures the first and last photos stop exactly in the center of the screen
      className="flex w-max gap-10 px-[calc(50vw-88px)] pt-4 md:gap-20 md:px-[calc(50vw-120px)]" data-id="element-145">
          {songs.map((song, index) => <SongPhoto key={song.id} song={song} index={index} isSelected={selectedSong.id === song.id} onClick={() => onSelect(song)} data-id="element-146" />)}
        </motion.div>
      </div>
    </div>;
}